/* ============================================================================
   HAKI — Our Story (editorial) choreography.
   Desktop (fine pointer, >=1024px, motion OK): a full-viewport scroll-JACKED slider
   (Segment-style) — the section is one viewport; the wheel/keys advance exactly one
   slide per gesture, and each transition plays at a FIXED duration via a GSAP tween,
   so fast scrolling queues at a capped pace instead of scrubbing to the bottom. Native
   scroll is released at the first/last slide (header/footer). Otherwise the markup
   stays a readable static stack with a light IntersectionObserver entrance (skipped
   under reduced motion). Uses the theme's global GSAP for the tween (no new dependency).
   Fully torn down / rebuilt for the Shopify theme editor.
   ============================================================================ */
(function () {
  'use strict';

  var DESKTOP_MIN = 1024;
  var instances = {}; // sectionId -> instance

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function cinematicAllowed() {
    var fine = window.matchMedia && window.matchMedia('(min-width: ' + DESKTOP_MIN + 'px) and (pointer: fine)').matches;
    return fine && !reducedMotion();
  }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function easeInOut(t) { t = clamp(t, 0, 1); return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }

  /* ----------------------------------------------------------- collect a scene */
  function readScene(sceneEl) {
    return {
      el: sceneEl,
      lines: Array.prototype.slice.call(sceneEl.querySelectorAll('.osed__lift')),
      img: sceneEl.querySelector('.osed__img'),
      content: sceneEl.querySelector('.osed__content'),
      gitems: Array.prototype.slice.call(sceneEl.querySelectorAll('.osed__gallery-item')),
      gimgs: Array.prototype.slice.call(sceneEl.querySelectorAll('.osed__gimg')),
      fades: Array.prototype.slice.call(sceneEl.querySelectorAll('[data-osed-body], [data-osed-cta]')),
      date: sceneEl.getAttribute('data-date') || ''
    };
  }

  /* lazy-mount the decorative collage: promote data-src -> src only for the current
     +/- adjacent chapters, so ~70 thumbs never load up front. The narrow static stack
     mounts a LITE subset (first few photos, right sizes attr) via its own observer;
     the wide static / reduced-motion stack still never calls this. */
  function mountScene(s, limit, sizesVal) {
    var n = (limit && limit < s.gimgs.length) ? limit : s.gimgs.length;
    for (var k = 0; k < n; k++) {
      var img = s.gimgs[k];
      if (sizesVal) img.sizes = sizesVal;              // set before src so the browser picks the right source
      if (!img.getAttribute('data-src')) continue;     // already mounted (or nothing to mount)
      img.src = img.getAttribute('data-src');
      var ss = img.getAttribute('data-srcset');
      if (ss) img.srcset = ss;
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
    }
  }
  function mountNearby(inst, idx) {
    for (var j = idx - 1; j <= idx + 1; j++) {
      if (inst.scenes[j]) mountScene(inst.scenes[j]);
    }
  }

  /* Off-stage scenes are opacity:0 but would stay keyboard-focusable (hero CTA,
     relocated footer links). inert removes them from tab order + the a11y tree;
     browsers without inert fall back to visibility (which also blocks focus)
     without ever touching opacity, so the active scene's fade is never fought. */
  function setSceneInert(el, on) {
    try {
      if ('inert' in el) { el.inert = on; return; }
    } catch (e) { /* fall through to the visibility fallback */ }
    el.style.visibility = on ? 'hidden' : '';
  }

  /* ------------------------------------------------------------- per-frame draw */
  function render(inst, progress) {
    var N = inst.count;
    var pos = progress * (N - 1); // focal scene position, 0..N-1
    var cur = clamp(Math.round(pos), 0, N - 1);

    for (var i = 0; i < N; i++) {
      var s = inst.scenes[i];
      var d = pos - i;            // signed distance from focal
      var ad = Math.abs(d);
      var el = s.el;

      if (d <= -1 || d >= 1) {
        // not entered yet (-1) or fully covered by a later scene (>=1): don't paint
        el.style.opacity = '0';
        el.style.pointerEvents = 'none';
        setSceneInert(el, true); // keep keyboard focus out of invisible scenes
        if (d <= -1 && i > 0) el.style.clipPath = 'inset(0px 0px 100% 0px)';
        continue;
      }
      setSceneInert(el, false);
      el.style.opacity = '1';
      el.style.pointerEvents = ad < 0.5 ? 'auto' : 'none';

      // scene-block curtain reveal (top -> down) for every scene after the first
      if (i > 0) {
        var reveal = easeInOut(clamp(pos - (i - 1), 0, 1));
        el.style.clipPath = 'inset(0px 0px ' + ((1 - reveal) * 100) + '% 0px)';
      }

      // media ken-burns + opposite-direction parallax (bg drifts one way, the
      // foreground image + text drift the other) — depth between layers.
      if (s.img) {
        var scale = 1 + clamp(ad, 0, 1.4) * 0.055;
        var py = clamp(d, -1.3, 1.3) * 26;
        var bgx = d * -26;
        s.img.style.transform = 'translate3d(' + bgx.toFixed(1) + 'px,' + (-py).toFixed(1) + 'px,0) scale(' + scale.toFixed(4) + ')';
      }
      if (s.content) { s.content.style.transform = 'translate3d(' + (d * 22).toFixed(1) + 'px,0,0)'; }
      for (var gi = 0; gi < s.gitems.length; gi++) {
        var rate = 46 + gi * 9;           // each image drifts at a slightly different rate -> depth (tuned for up to 10 layers)
        s.gitems[gi].style.transform = 'translate3d(' + (d * rate).toFixed(1) + 'px,' + (d * (8 + gi * 3)).toFixed(1) + 'px,0)';
      }

      // heading / eyebrow lines: lift in (incoming) or lift away (outgoing)
      for (var li = 0; li < s.lines.length; li++) {
        var stag = li * 0.05;
        var y;
        if (d < 0) { y = (1 - easeInOut(clamp(d + 1 - stag, 0, 1))) * 116; }
        else { y = -easeInOut(clamp(d - stag, 0, 1)) * 116; }
        s.lines[li].style.transform = 'translateY(' + y.toFixed(2) + '%)';
      }

      // body copy + buttons: quieter fade/drift
      var bodyOp = clamp(1 - ad * 1.3, 0, 1);
      for (var fi = 0; fi < s.fades.length; fi++) {
        s.fades[fi].style.opacity = bodyOp;
        s.fades[fi].style.transform = 'translateY(' + (d * -16).toFixed(1) + 'px)';
      }

      if ((Math.round(pos) === i) !== el.classList.contains('is-active')) {
        el.classList.toggle('is-active', Math.round(pos) === i);
      }
    }

    // chrome — the footer slide is excluded from the displayed count and hides the
    // chrome entirely (counter + progress bar), so total stays at hero + chapters.
    var lastContent = inst.footerScene ? inst.count - 2 : inst.count - 1; // index of final non-footer scene
    var atFooter = !!inst.footerScene && cur > lastContent;
    inst.section.classList.toggle('osed-at-footer', atFooter);
    var shown = Math.min(cur + 1, lastContent + 1);
    inst.counter.textContent = shown < 10 ? '0' + shown : '' + shown;
    inst.caption.textContent = inst.scenes[cur].date;
    inst.fill.style.transform = 'scaleX(' + clamp(pos / Math.max(1, lastContent), 0, 1).toFixed(4) + ')';
  }

  /* ---------------------------------------------------------- footer-as-a-slide */
  function pad2(n) { return n < 10 ? '0' + n : '' + n; }
  // Cinematic only: pull the site's real footer into the slide sequence as the final
  // scene (so it slides in like the others), preserving all of its content/settings.
  // Static / mobile leaves the footer untouched at the bottom of the stack.
  function relocateFooter(inst) {
    if (inst.footerScene || !inst.scenesEl) return;
    var footer = document.querySelector('.hk-footer');
    if (!footer) return;
    inst.footerHome = { parent: footer.parentNode, next: footer.nextSibling };
    var scene = document.createElement('article');
    scene.className = 'osed__scene osed__scene--footer';
    scene.setAttribute('data-osed-scene', inst.count);
    scene.setAttribute('data-align', 'center');
    scene.setAttribute('data-date', '');
    scene.appendChild(footer);
    inst.scenesEl.appendChild(scene);
    inst.footerScene = scene;
    // slide-only newsletter copy (the signup is the focus of the footer slide)
    var nh = footer.querySelector('.hk-footer__news .hk-footer__heading');
    var ns = footer.querySelector('.hk-footer__news .hk-footer__news-text');
    if (nh) { inst._origNewsH = nh.textContent; nh.textContent = 'Want to know what happens next?'; }
    if (ns) { inst._origNewsS = ns.textContent; ns.textContent = 'Join the community to stay in touch.'; }
    inst.scenes.push(readScene(scene));
    inst.count += 1;
    // NOTE: the footer is deliberately excluded from the DISPLAYED total — leave
    // counterTotal at the server value (hero + chapters). inst.count still grows so
    // the footer is navigable; the chrome (counter + progress) is hidden on it.
    inst.section.style.setProperty('--osed-count', inst.count);
  }
  function restoreFooter(inst) {
    if (!inst.footerScene) return;
    var footer = inst.footerScene.querySelector('.hk-footer');
    if (footer) {
      // revert the slide-only newsletter copy before handing the footer back
      var nh = footer.querySelector('.hk-footer__news .hk-footer__heading');
      var ns = footer.querySelector('.hk-footer__news .hk-footer__news-text');
      if (nh && inst._origNewsH != null) nh.textContent = inst._origNewsH;
      if (ns && inst._origNewsS != null) ns.textContent = inst._origNewsS;
    }
    if (footer && inst.footerHome && inst.footerHome.parent) {
      inst.footerHome.parent.insertBefore(footer, inst.footerHome.next || null);
    }
    if (inst.footerScene.parentNode) inst.footerScene.parentNode.removeChild(inst.footerScene);
    inst.footerScene = null; inst.footerHome = null;
    inst.scenes.pop();
    inst.count -= 1;
    inst.section.style.setProperty('--osed-count', inst.count);
  }

  /* --------------------------------------------------------------- cinematic on */
  function enableCinematic(inst) {
    var section = inst.section;
    section.classList.add('is-cinematic');
    section.classList.remove('osed--observe');
    document.documentElement.classList.add('osed-no-scrollbar'); // Segment-style: hide the scrollbar
    if (inst.io) { inst.io.disconnect(); inst.io = null; }
    if (inst.ioHero) { inst.ioHero.disconnect(); inst.ioHero = null; }

    relocateFooter(inst);   // append the footer scene before the first render

    inst.current = 0;
    inst.progress = 0;
    inst.animating = false;
    render(inst, 0);
    mountNearby(inst, 0);   // load the first chapter's collage (+ its neighbour) up front

    // chrome shows whenever the section is in view (present from the first frame).
    if ('IntersectionObserver' in window) {
      inst.chromeIO = new IntersectionObserver(function (entries) {
        section.classList.toggle('osed-chrome-on', entries[0].isIntersecting);
      }, { threshold: 0 });
      inst.chromeIO.observe(section);
    } else {
      section.classList.add('osed-chrome-on');
    }

    // ----- scroll-jack: one wheel gesture advances exactly one slide; each transition
    //       plays at a FIXED duration, so fast scrolling queues at a capped pace instead
    //       of scrubbing straight to the bottom. Native scroll is released at the edges.
    inst.onWheel = function (e) { jackWheel(inst, e); };
    window.addEventListener('wheel', inst.onWheel, { passive: false });
    inst.onKey = function (e) { jackKey(inst, e); };
    window.addEventListener('keydown', inst.onKey);

    var refresh = function () { if (inst.mode === 'cinematic') render(inst, inst.progress); };
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh).catch(function () {});
    window.addEventListener('load', refresh, { once: true });
    inst._refresh = refresh;
  }

  // the section is the active full-screen stage only when it exactly fills the viewport
  function sectionEngaged(section) {
    var r = section.getBoundingClientRect();
    return r.top <= 1 && r.bottom >= window.innerHeight - 1;
  }

  var GESTURE_GAP = 150; // ms of wheel quiet that separates one gesture from the next

  function jackWheel(inst, e) {
    if (!sectionEngaged(inst.section)) return;           // not engaged -> native scroll (footer/header)
    var dir = e.deltaY > 0 ? 1 : (e.deltaY < 0 ? -1 : 0);
    if (!dir) return;
    if (dir > 0 && inst.current >= inst.count - 1) return; // at last slide, scroll down -> release
    if (dir < 0 && inst.current <= 0) return;              // at first slide, scroll up -> release
    e.preventDefault();                                    // jack: block native scroll within the sequence
    var now = (window.performance || Date).now();
    // a brief quiet between wheel events ends one gesture and lets the next one advance;
    // a continuous burst (fast flick / momentum) stays a single gesture = a single slide.
    if (now - inst.lastWheel > GESTURE_GAP) inst.gestureDone = false;
    inst.lastWheel = now;
    if (inst.animating || inst.gestureDone) return;        // mid-transition, or this gesture already advanced
    inst.gestureDone = true;
    goToSlide(inst, inst.current + dir);
  }

  function jackKey(inst, e) {
    // never hijack keys meant for a form control / button / editable region
    var t = e.target;
    if (t && t.closest && t.closest('input, textarea, select, button, [contenteditable]')) return;
    if (!sectionEngaged(inst.section) || inst.animating) return;
    var down = e.key === 'ArrowDown' || e.key === 'PageDown' || (e.key === ' ' && !e.shiftKey);
    var up = e.key === 'ArrowUp' || e.key === 'PageUp' || (e.key === ' ' && e.shiftKey);
    if (down && inst.current < inst.count - 1) { e.preventDefault(); goToSlide(inst, inst.current + 1); }
    else if (up && inst.current > 0) { e.preventDefault(); goToSlide(inst, inst.current - 1); }
  }

  function goToSlide(inst, idx) {
    idx = clamp(idx, 0, inst.count - 1);
    if (idx === inst.current) return;
    inst.animating = true;
    inst.current = idx;
    mountNearby(inst, idx); // load the collage for the slide we're moving to (+ its neighbour) ahead of the reveal
    var toP = inst.count > 1 ? idx / (inst.count - 1) : 0;
    // transition feel (tune to taste): TRANSITION_DUR seconds, cubic in-out (power3) so it
    // eases in/out softly with a snappy middle. The rAF fallback below mirrors both.
    if (window.gsap) {
      window.gsap.to(inst, {
        progress: toP, duration: 0.62, ease: 'power3.inOut', overwrite: true,
        onUpdate: function () { render(inst, inst.progress); },
        onComplete: function () { inst.animating = false; } // gesture-gap (not a cooldown) caps the pace
      });
    } else {
      // rAF fallback (no GSAP) — fixed-duration eased tween (cubic in-out == power3.inOut)
      var fromP = inst.progress, start = (window.performance || Date).now(), dur = 620;
      (function step(now) {
        now = now || (window.performance || Date).now();
        var t = clamp((now - start) / dur, 0, 1);
        t = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        inst.progress = fromP + (toP - fromP) * t;
        render(inst, inst.progress);
        if (t < 1) requestAnimationFrame(step); else inst.animating = false;
      })();
    }
  }

  /* ------------------------------------------------------------------ static on */
  function enableStatic(inst) {
    inst.section.classList.remove('is-cinematic');
    if (reducedMotion() || !('IntersectionObserver' in window)) return; // fully readable, no entrance/parallax
    inst.section.classList.add('osed--observe');
    // both directions: scenes animate in on entry AND back out on exit, so
    // scrolling up replays the choreography (no unobserve). Keyed on each scene's
    // CONTENT block, not the (now very tall) scene box, and only once it clears
    // the bottom ~30% of the viewport - otherwise the date-header lift plays
    // while the text is still below the fold and lands pre-finished.
    var onContent = function (entries) {
      entries.forEach(function (e) {
        var sc = e.target.closest ? e.target.closest('[data-osed-scene]') : null;
        (sc || e.target).classList.toggle('is-inview', e.isIntersecting);
      });
    };
    inst.io = new IntersectionObserver(onContent, { rootMargin: '0px 0px -30% 0px', threshold: 0 });
    // hero is exempt from the deferral: its title sits in the bottom 30% of the
    // first viewport and must lift in on load, not wait for a scroll
    inst.ioHero = new IntersectionObserver(onContent, { threshold: 0.05 });
    inst.scenes.forEach(function (s) {
      var target = s.content || s.el;
      if (s.el.classList.contains('osed__scene--hero')) inst.ioHero.observe(target);
      else inst.io.observe(target);
    });

    // Collage LITE (narrow static stack only): mount the first few collage photos per
    // scene as it approaches (one viewport ahead), then flip .is-mounted so the CSS
    // shows the strip. 6 small thumbs per chapter instead of the desktop 10; the
    // hero mounts its two whole photos. Wide static stays collage-free as before.
    var narrow = window.matchMedia && window.matchMedia('(max-width: ' + (DESKTOP_MIN - 1) + 'px)').matches;
    if (narrow) {
      var LITE_MAX = 6;
      inst.mountIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          for (var i = 0; i < inst.scenes.length; i++) {
            if (inst.scenes[i].el !== en.target) continue;
            var hero = en.target.classList.contains('osed__scene--hero');
            mountScene(inst.scenes[i], hero ? 2 : LITE_MAX, hero ? '62vw' : '58vw');
            en.target.classList.add('is-mounted');
            break;
          }
          inst.mountIO.unobserve(en.target);
        });
      }, { rootMargin: '120% 0px' });
      inst.scenes.forEach(function (s) { if (s.gimgs.length) inst.mountIO.observe(s.el); });

      // per-photo choreography: each collage photo lifts in when it enters the
      // viewport and drops back out when it leaves, both scroll directions
      inst.itemIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { en.target.classList.toggle('is-in', en.isIntersecting); });
      }, { threshold: 0.15 });
      inst.scenes.forEach(function (s) {
        for (var gi = 0; gi < Math.min(s.gitems.length, LITE_MAX); gi++) inst.itemIO.observe(s.gitems[gi]);
      });

      // closing CTA: give the real footer's newsletter the cinematic slide's copy +
      // hero styling (class-gated CSS above; restored verbatim in teardownMode)
      var footer = document.querySelector('.hk-footer');
      if (footer) {
        inst.ctaFooter = footer;
        footer.classList.add('hk-footer--osed-cta');
        var nh = footer.querySelector('.hk-footer__news .hk-footer__heading');
        var ns = footer.querySelector('.hk-footer__news .hk-footer__news-text');
        if (nh) { inst._ctaH = nh.textContent; nh.textContent = 'Want to know what happens next?'; }
        if (ns) { inst._ctaS = ns.textContent; ns.textContent = 'Join the community to stay in touch.'; }
        // the entrance animation itself is native + site-wide now (haki-footer
        // .liquid, -40% trigger); this page only re-sequences its delays in CSS
      }
    }

    // Subtle background parallax for the mobile/static stack: each chapter's full-bleed
    // photo drifts opposite to scroll. Transform-only + rAF-throttled + in-view scenes
    // only (cheap on mobile); loads NO extra images (the foreground collage stays off
    // mobile for ecom performance). Skipped above under reduced-motion.
    var ticking = false;
    function parallax() {
      ticking = false;
      var vh = window.innerHeight, vc = vh / 2;
      var focal = -1;
      for (var i = 0; i < inst.scenes.length; i++) {
        var s = inst.scenes[i];
        var r = s.el.getBoundingClientRect();
        if (r.top <= vc && r.bottom > vc) focal = i;      // scene under the viewport centre
        if (!s.img) continue;
        if (r.bottom < -40 || r.top > vh + 40) continue; // off-screen → skip
        var rel = clamp(((r.top + r.height / 2) - vc) / vh, -1, 1);
        s.img.style.transform = 'translate3d(0,' + (rel * -26).toFixed(1) + 'px,0) scale(1.12)';
      }
      // narrow-stack chrome: counter follows the focal scene, fill = scroll progress,
      // shown only while the section actually covers the viewport centre (an IO on
      // the section stays "intersecting" from behind the fixed header, so the chrome
      // would otherwise linger over the footer)
      if (narrow) {
        if (focal >= 0) inst.counter.textContent = pad2(focal + 1);
        var sr = inst.section.getBoundingClientRect();
        var span = Math.max(1, sr.height - vh);
        inst.fill.style.transform = 'scaleX(' + clamp(-sr.top / span, 0, 1).toFixed(4) + ')';
        inst.section.classList.toggle('osed-chrome-on', sr.top < vc && sr.bottom > vc);
      }
    }
    inst._onScroll = function () { if (!ticking) { ticking = true; requestAnimationFrame(parallax); } };
    window.addEventListener('scroll', inst._onScroll, { passive: true });
    window.addEventListener('resize', inst._onScroll, { passive: true });
    parallax();
  }

  /* ------------------------------------------------------------- mode selection */
  function applyMode(inst) {
    if (cinematicAllowed()) {
      if (inst.mode !== 'cinematic') { teardownMode(inst); inst.mode = 'cinematic'; enableCinematic(inst); }
    } else {
      if (inst.mode !== 'static') { teardownMode(inst); inst.mode = 'static'; enableStatic(inst); }
    }
  }
  function teardownMode(inst) {
    restoreFooter(inst); // put the real footer back where it lives before clearing scenes
    if (window.gsap) window.gsap.killTweensOf(inst);
    if (inst.onWheel) { window.removeEventListener('wheel', inst.onWheel); inst.onWheel = null; }
    if (inst.onKey) { window.removeEventListener('keydown', inst.onKey); inst.onKey = null; }
    if (inst.io) { inst.io.disconnect(); inst.io = null; }
    if (inst.ioHero) { inst.ioHero.disconnect(); inst.ioHero = null; }
    if (inst.mountIO) { inst.mountIO.disconnect(); inst.mountIO = null; }
    if (inst.itemIO) { inst.itemIO.disconnect(); inst.itemIO = null; }
    if (inst.chromeIO) { inst.chromeIO.disconnect(); inst.chromeIO = null; }
    if (inst.ctaFooter) {
      inst.ctaFooter.classList.remove('hk-footer--osed-cta');
      var cnh = inst.ctaFooter.querySelector('.hk-footer__news .hk-footer__heading');
      var cns = inst.ctaFooter.querySelector('.hk-footer__news .hk-footer__news-text');
      if (cnh && inst._ctaH != null) cnh.textContent = inst._ctaH;
      if (cns && inst._ctaS != null) cns.textContent = inst._ctaS;
      inst.ctaFooter = null; inst._ctaH = null; inst._ctaS = null;
    }
    if (inst._onScroll) { window.removeEventListener('scroll', inst._onScroll); window.removeEventListener('resize', inst._onScroll); inst._onScroll = null; }
    if (inst._refresh) { window.removeEventListener('load', inst._refresh); inst._refresh = null; }
    inst.animating = false;
    document.documentElement.classList.remove('osed-no-scrollbar');
    inst.section.classList.remove('is-cinematic', 'osed--observe', 'osed-chrome-on');
    // clear inline styles JS applied so the static layout reads cleanly
    // (cssText='' also clears the visibility fallback; inert needs its own reset)
    inst.scenes.forEach(function (s) {
      s.el.style.cssText = '';
      setSceneInert(s.el, false);
      if (s.img) s.img.style.transform = '';
      if (s.content) s.content.style.transform = '';
      s.gitems.forEach(function (g) { g.style.transform = ''; g.classList.remove('is-in'); });
      s.lines.forEach(function (l) { l.style.transform = ''; });
      s.fades.forEach(function (f) { f.style.opacity = ''; f.style.transform = ''; });
      s.el.classList.remove('is-active', 'is-inview');
    });
    inst.mode = null;
  }

  /* ------------------------------------------------------------------- init one */
  function init(section) {
    if (!section || section.__osed) return;
    var id = section.id;
    var sceneEls = Array.prototype.slice.call(section.querySelectorAll('[data-osed-scene]'));
    if (!sceneEls.length) return;

    var inst = {
      section: section,
      scenesEl: section.querySelector('[data-osed-scenes]'),
      scenes: sceneEls.map(readScene),
      count: parseInt(section.getAttribute('data-scene-count'), 10) || sceneEls.length,
      counter: section.querySelector('[data-osed-counter]'),
      counterTotal: section.querySelector('.osed__counter-total'),
      caption: section.querySelector('[data-osed-caption]'),
      fill: section.querySelector('[data-osed-progress]'),
      mode: null, io: null, ioHero: null, mountIO: null, itemIO: null, chromeIO: null, _refresh: null,
      ctaFooter: null, _ctaH: null, _ctaS: null,
      current: 0, progress: 0, animating: false, onWheel: null, onKey: null,
      lastWheel: 0, gestureDone: false,
      footerScene: null, footerHome: null
    };
    section.style.setProperty('--osed-count', inst.count);
    section.__osed = inst;
    instances[id] = inst;

    applyMode(inst);

    inst._onResize = debounce(function () { applyMode(inst); }, 200);
    window.addEventListener('resize', inst._onResize, { passive: true });
  }

  function destroy(section) {
    var inst = section && section.__osed;
    if (!inst) return;
    teardownMode(inst);
    if (inst._onResize) window.removeEventListener('resize', inst._onResize);
    delete instances[section.id];
    section.__osed = null;
  }

  function debounce(fn, ms) {
    var t; return function () { clearTimeout(t); t = setTimeout(fn, ms); };
  }

  /* ------------------------------------------------------------------ bootstrap */
  function boot() {
    Array.prototype.slice.call(document.querySelectorAll('[data-osed]')).forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* --------------------------------------------------------- Shopify theme editor */
  document.addEventListener('shopify:section:load', function (e) {
    var sec = e.target.querySelector('[data-osed]') || (e.target.matches && e.target.matches('[data-osed]') ? e.target : null);
    if (sec) init(sec);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var sec = e.target.querySelector('[data-osed]') || (e.target.matches && e.target.matches('[data-osed]') ? e.target : null);
    if (sec) destroy(sec);
  });
  // selecting a chapter block in the editor jumps the page to that scene
  document.addEventListener('shopify:block:select', function (e) {
    var sceneEl = e.target.closest ? e.target.closest('[data-osed-scene]') : null;
    var secEl = e.target.closest ? e.target.closest('[data-osed]') : null;
    if (!sceneEl || !secEl) return;
    var inst = secEl.__osed;
    var i = parseInt(sceneEl.getAttribute('data-osed-scene'), 10) || 0;
    if (!inst || inst.mode !== 'cinematic') { sceneEl.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
    // jump the slider to the selected chapter scene
    window.scrollTo({ top: 0, behavior: 'auto' });
    inst.animating = false;
    goToSlide(inst, i);
  });
})();
